import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { collection, addDoc, Timestamp, query, getDocs, orderBy, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { validateSlug, generateSlugFromName } from '../lib/slug-validation';
import { LogOut, Home, Plus, Building2, CheckCircle2, List, ExternalLink, Ban, PlayCircle, Loader2, FileText } from 'lucide-react';
import BlogManagement from '../components/admin/BlogManagement';

// Helper function to format status for display
const formatStatus = (status: string): string => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Customer schema
const customerSchema = z.object({
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  slug: z.string().min(3, 'Slug must be at least 3 characters'),
  domain: z.string().email('Must be a valid domain (e.g., acme.com)').or(z.string().regex(/^[a-z0-9-]+\.[a-z]{2,}$/i, 'Must be a valid domain')),
  brandColor: z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'Must be a valid hex color (e.g., #FF5733)'),
});

type CustomerFormData = z.infer<typeof customerSchema>;

interface Customer {
  id: string;
  companyName: string;
  slug: string;
  domain: string;
  brandColor: string;
  plan: string;
  status: string;
  createdAt: Timestamp;
}

type TabType = 'customers' | 'blog';

export default function SuperAdminPortal() {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('customers');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      companyName: '',
      slug: '',
      domain: '',
      brandColor: '#3B82F6', // Default blue
    },
  });

  const slug = watch('slug');

  // Fetch customers on mount
  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const customersRef = collection(db, 'customers');
      const q = query(customersRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const customersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Customer[];

      setCustomers(customersList);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  // Auto-generate slug from company name
  const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setValue('companyName', name);

    // Auto-generate slug if it hasn't been manually edited
    const suggestedSlug = generateSlugFromName(name);
    setValue('slug', suggestedSlug);
  };

  // Validate slug on change
  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSlug = e.target.value.toLowerCase();
    setValue('slug', newSlug);

    const validation = validateSlug(newSlug);
    if (!validation.valid) {
      setSlugError(validation.error || null);
    } else {
      setSlugError(null);
    }
  };

  const handleToggleCustomerStatus = async (customerId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    const confirmed = window.confirm(
      `Are you sure you want to ${newStatus === 'suspended' ? 'suspend' : 'reactivate'} this customer?`
    );

    if (!confirmed) return;

    setUpdatingStatus(customerId);
    try {
      await updateDoc(doc(db, 'customers', customerId), {
        status: newStatus,
        updatedAt: Timestamp.now(),
      });

      // Update local state
      setCustomers(prev =>
        prev.map(c => c.id === customerId ? { ...c, status: newStatus } : c)
      );
    } catch (error) {
      console.error('Error updating customer status:', error);
      toast.error('Failed to update customer status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const onSubmit = async (data: CustomerFormData) => {
    // Final slug validation
    const slugValidation = validateSlug(data.slug);
    if (!slugValidation.valid) {
      setSlugError(slugValidation.error || 'Invalid slug');
      return;
    }

    // Check if slug already exists
    const customersRef = collection(db, 'customers');
    const q = query(customersRef);
    const snapshot = await getDocs(q);
    const existingSlugs = snapshot.docs.map(doc => doc.data().slug);

    if (existingSlugs.includes(data.slug)) {
      setSlugError('This slug is already taken');
      return;
    }

    setIsSubmitting(true);
    setSlugError(null);

    try {
      // Create customer document
      await addDoc(collection(db, 'customers'), {
        companyName: data.companyName,
        slug: data.slug,
        domain: data.domain,
        plan: 'free',
        brandColor: data.brandColor,
        status: 'active',
        createdAt: Timestamp.now(),
        // Optional fields can be added later
        logoUrl: null,
        faviconUrl: null,
        accentColor: null,
        // Default banner content
        bannerTitle: 'Building Better Products Together',
        bannerDescription: 'Your feedback shapes our roadmap. Share your ideas, vote on features that matter to you, and help us build a product you\'ll love.',
      });

      setSuccess(true);
      reset();

      // Refresh customers list
      fetchCustomers();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error('Failed to create customer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="h-6 w-6 text-primary" />
              <div>
                <h1 className="text-2xl font-bold text-foreground">Super Admin</h1>
                <p className="text-sm text-muted-foreground">ProductWorkshop Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                {user?.email}
              </div>
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <Home className="h-4 w-4 mr-2" />
                  Home
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={signOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('customers')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === 'customers'
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Building2 className="h-4 w-4" />
              Customers
            </button>
            <button
              onClick={() => setActiveTab('blog')}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                activeTab === 'blog'
                  ? 'border-primary text-primary font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <FileText className="h-4 w-4" />
              Blog
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'blog' ? (
          <BlogManagement />
        ) : (
        <div className="space-y-8 max-w-4xl">{/* Constrain width for customers tab */}
          {/* Success Message */}
          {success && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Customer created successfully!</span>
              </div>
            </div>
          )}

          {/* Existing Customers */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <List className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold text-foreground">
                Existing Customers
              </h2>
              <span className="text-sm text-muted-foreground ml-auto">
                {customers.length} total
              </span>
            </div>

            {loadingCustomers ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading customers...
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No customers yet. Create your first customer below.
              </div>
            ) : (
              <div className="space-y-3">
                {customers.map((customer) => (
                  <div
                    key={customer.id}
                    className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground">
                            {customer.companyName}
                          </h3>
                          <div
                            className="w-4 h-4 rounded border border-border"
                            style={{ backgroundColor: customer.brandColor }}
                            title={customer.brandColor}
                          />
                          {customer.status === 'suspended' && (
                            <span className="px-2 py-0.5 bg-red-500/10 border border-red-500/20 rounded text-xs font-medium text-red-600 dark:text-red-400">
                              Suspended
                            </span>
                          )}
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <span>Slug:</span>
                            <code className="bg-muted px-1 py-0.5 rounded">
                              {customer.slug}
                            </code>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <span>Domain:</span>
                            <code className="bg-muted px-1 py-0.5 rounded">
                              {customer.domain}
                            </code>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <span>Plan:</span>
                            <span className="capitalize">{customer.plan}</span>
                            <span>•</span>
                            <span>{formatStatus(customer.status)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a
                          href={`/${customer.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <Button variant="outline" size="sm" disabled={customer.status === 'suspended'}>
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Portal
                          </Button>
                        </a>
                        <Link to={`/${customer.slug}/admin`}>
                          <Button variant="outline" size="sm" disabled={customer.status === 'suspended'}>
                            Admin
                          </Button>
                        </Link>
                        <Button
                          variant={customer.status === 'active' ? 'destructive' : 'default'}
                          size="sm"
                          onClick={() => handleToggleCustomerStatus(customer.id, customer.status)}
                          disabled={updatingStatus === customer.id}
                        >
                          {updatingStatus === customer.id ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : customer.status === 'active' ? (
                            <Ban className="h-3 w-3 mr-1" />
                          ) : (
                            <PlayCircle className="h-3 w-3 mr-1" />
                          )}
                          {customer.status === 'active' ? 'Suspend' : 'Reactivate'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create Customer Form */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-6">
              <Plus className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold text-foreground">
                Create New Customer
              </h2>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Company Name */}
              <div className="space-y-2">
                <Label htmlFor="companyName">
                  Company Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="companyName"
                  {...register('companyName')}
                  onChange={handleCompanyNameChange}
                  placeholder="Acme Corporation"
                  className={errors.companyName ? 'border-destructive' : ''}
                />
                {errors.companyName && (
                  <p className="text-sm text-destructive">{errors.companyName.message}</p>
                )}
              </div>

              {/* Slug */}
              <div className="space-y-2">
                <Label htmlFor="slug">
                  URL Slug <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="slug"
                  {...register('slug')}
                  onChange={handleSlugChange}
                  placeholder="acme-corp"
                  className={errors.slug || slugError ? 'border-destructive' : ''}
                />
                <p className="text-xs text-muted-foreground">
                  Will be used in URL: <code className="bg-muted px-1 py-0.5 rounded">getproductworkshop.com/{slug || 'acme-corp'}</code>
                </p>
                {(errors.slug || slugError) && (
                  <p className="text-sm text-destructive">{errors.slug?.message || slugError}</p>
                )}
              </div>

              {/* Domain */}
              <div className="space-y-2">
                <Label htmlFor="domain">
                  Company Domain <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="domain"
                  {...register('domain')}
                  placeholder="acme.com"
                  className={errors.domain ? 'border-destructive' : ''}
                />
                <p className="text-xs text-muted-foreground">
                  Used for admin email validation
                </p>
                {errors.domain && (
                  <p className="text-sm text-destructive">{errors.domain.message}</p>
                )}
              </div>

              {/* Brand Color */}
              <div className="space-y-2">
                <Label htmlFor="brandColor">
                  Brand Color <span className="text-destructive">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="brandColor"
                    {...register('brandColor')}
                    placeholder="#3B82F6"
                    className={`flex-1 ${errors.brandColor ? 'border-destructive' : ''}`}
                  />
                  <div
                    className="w-12 h-10 rounded border border-border"
                    style={{ backgroundColor: watch('brandColor') || '#3B82F6' }}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Primary brand color in hex format
                </p>
                {errors.brandColor && (
                  <p className="text-sm text-destructive">{errors.brandColor.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Customer'}
              </Button>
            </form>
          </div>

          {/* Info Card */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-2">
              Next Steps After Creating Customer
            </h3>
            <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
              <li>✅ Customer will be accessible at: <code className="bg-muted px-1 py-0.5 rounded">/{slug || 'slug'}</code></li>
              <li>✅ Customer admin portal at: <code className="bg-muted px-1 py-0.5 rounded">/{slug || 'slug'}/admin</code></li>
              <li>⏳ Upload logo and favicon (coming soon)</li>
              <li>⏳ Invite customer admins (coming soon)</li>
              <li>⏳ Configure white-label settings (coming soon)</li>
            </ul>
          </div>
        </div>
        )}
      </main>
    </div>
  );
}
